// src/pages/dashboard/Wallets.tsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import ReactCountryFlag from 'react-country-flag';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../lib/utils';
import '../../assets/styles/wallets-button.css';
import { mainService } from '../../services/api/main.service';
import { getServerUrl } from '../../config/server.config';
import WalletDetailsItem from '../../components/dashboard/wallet/walletDetailsItem';
import FiatWalletItem from '../../components/dashboard/wallet/fiatWalletItem';
import {FiatExchangeRates} from '../../types/financial.types';
import { WalletData } from '../../types/wallet.types';
import {MainWallet} from "../../types/rail.types"
import {RailAccounts} from '../../types/rail.types'
import { 
  BitcoinIcon, 
  EthereumIcon, 
  TetherIcon,
  BinanceIcon,
  TronIcon, 
} from '../../components/ui/Icons';
import type { RootState } from '../../store';
import { useCryptoWallets, useMainWallets } from '../../hooks/hooks';
import { IsLoading } from '../../components/ui/isLoading';
import { getNetworkInfo, getWarningMessageKey } from '../../utils/cryptoNetworkUtils';
interface CryptoPrices {
  BTC: number;
  ETH: number;
  BNB: number;
  USDT: number;
  TRX: number;
}
interface WalletItemType {
  id:string;
  userId:string;
  network:string;
  address:string;
  currency:string;
  balance:string;
}
const getReclassifiedAccounts = (railAccounts: RailAccounts): RailAccounts => {
  const fiatWallets = [...railAccounts.fiat];
  const cryptoWallets = [...railAccounts.crypto];
  const walletsToMove = fiatWallets.filter(wallet => 
    wallet.type === 'TRON_MAINNET_TRX' || 
    wallet.type === 'TRON_MAINNET_USDT'
  );
  const newFiatWallets = fiatWallets.filter(wallet => 
    wallet.type !== 'TRON_MAINNET_TRX' && 
    wallet.type !== 'TRON_MAINNET_USDT'
  );
  const newCryptoWallets = [...cryptoWallets, ...walletsToMove];
  const filteredFiatWallets = newFiatWallets.filter(wallet => 
    wallet.type === 'FIAT_MAINNET_USD' ||
    wallet.type === 'FIAT_MAINNET_EUR' ||
    wallet.type === 'FIAT_MAINNET_GBP'
  );
  const filteredCryptoWallets = newCryptoWallets.filter(wallet => 
    wallet.type === 'BITCOIN_MAINNET_BTC' ||
    wallet.type === 'ETHEREUM_MAINNET_ETH' ||
    wallet.type === 'ETHEREUM_MAINNET_USDT' ||
    wallet.type === 'ETHEREUM_MAINNET_USDC' ||
    wallet.type === 'TRON_MAINNET_TRX' ||
    wallet.type === 'TRON_MAINNET_USDT'
  );
  
  return {
    fiat: filteredFiatWallets,
    crypto: filteredCryptoWallets
  };
};
export default function Wallets() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: { cryptoWallets }, status } = useSelector((state: RootState) => state.assets);
  const { data: { cryptoPrices } } = useSelector((state: RootState) => state.setting);
  const [showQRCode, setShowQRCode] = useState<string | null>(null);
  const [qrCodeData, setQRCodeData] = useState<string | null>(null);
  const [qrCodeCurrency, setQRCodeCurrency] = useState<string>('');
  const [qrCodeNetwork, setQRCodeNetwork] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [walletList, setWalletList] = useState<WalletItemType[]>();
  const [totalBalance, setTotalBalance] = useState<number>();
  const [solPrice, setSolPrice] = useState<number>(0);

  // Get Zenus account from Redux store
  const { zenusAccount, masterCard } = useSelector((state: RootState) => state.assets.data);
  const fiatBalance = zenusAccount?.balance || 0;
  const mastercardBalance = masterCard?.balance || 0;
  // const [railAccounts, setRailAccounts] = useState<RailAccounts>({ fiat: [], crypto: [] }); 
//   const [calculatedBalances, setCalculatedBalances] = useState({
//     totalWealth: 0,
//     fiatBalance: 0,
//     cryptoBalance: 0
//   });
//  const [fiatExchangeRates, setFiatExchangeRates] = useState<FiatExchangeRates>({
//     USD: 1.0, // USD es la base
//     EUR: 1.08, // Valores por defecto
//     GBP: 1.27,
//     AED: 0.27,
//     MEX: 0.05,
//     PAN: 1.0
//   });
//   const [cryptoPrices, setCryptoPrices] = useState<CryptoPrices>({
//     BTC: 65000,
//     ETH: 4000,
//     BNB: 600,
//     USDT: 0.999,
//     TRX: 0.05
//   });
  const [tronWalletsData, setTronWalletsData] = useState<Record<string, any>>({});
  const [persistedUsdtBalances, setPersistedUsdtBalances] = useState<Record<string, string>>({});
  // const [virtualWallets, setVirtualWallets] = useState<WalletData[]>([]);
  const isMounted = useRef<boolean>(true);
  const tronFetchTimeoutRefs = useRef<Record<string, NodeJS.Timeout>>({});
  const calculationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const virtualWalletsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // const processingTronWalletsRef = useRef<boolean>(false);
  // const initialLoadRef = useRef(true);
  // const calculateTotals = () => {
  //     let totalCryptoValue = 0;
  //     let totalFiatValue = 0;
  //     const loadCachedExchangeRates = () => {
  //       const cachedRates = localStorage.getItem('fiat-rates');
  //       const cachedTimestamp = localStorage.getItem('fiat-rates-timestamp');
  //       const CACHE_DURATION = 3600000; // 1 hour in milliseconds
        
  //       const isCacheValid = cachedTimestamp && 
  //         (Date.now() - parseInt(cachedTimestamp) < CACHE_DURATION);
        
  //       if (cachedRates && isCacheValid) {
  //         setFiatExchangeRates(JSON.parse(cachedRates));
  //         return true;
  //       }
  //       return false;
  //     };
  //     const calculateFiatValues = () => {
  //       console.log(railAccounts);
  //       if (!railAccounts?.fiat?.length)  {refetchRailAccounts()};
  //       return railAccounts.fiat.reduce((total, wallet) => {
  //         if (!wallet?.availableBalance || !wallet?.currency) return total;
          
  //         const balance = parseFloat(wallet.availableBalance);
  //         if (isNaN(balance)) return total;
          
  //         const currency = wallet.currency;
  //         const exchangeRate = fiatExchangeRates[currency];
          
  //         if (exchangeRate) {
  //           return total + (balance * exchangeRate);
  //         }
          
  //         return total;
  //       }, 0);
  //     };
  //     const calculateRailCryptoValues = () => {
  //       if (!railAccounts?.crypto?.length) return 0;
        
  //       return railAccounts.crypto.reduce((total, wallet) => {
  //         if (!wallet?.availableBalance || !wallet?.currency) return total;
          
  //         const balance = parseFloat(wallet.availableBalance);
  //         if (isNaN(balance)) return total;
          
  //         const currency = wallet.currency.split('_')[0];
  //         const price = cryptoPrices[currency];
          
  //         if (price) {
  //           return total + (balance * price);
  //         }
          
  //         return total;
  //       }, 0);
  //     };
  //     const calculateCryptoWalletValues = () => {
  //       if (!dashboardData?.wallets?.length) return 0;
        
  //       return dashboardData.wallets.reduce((total, wallet) => {
  //         if (!wallet.balance || !wallet.currency) return total;
          
  //         const balance = parseFloat(wallet.balance);
  //         if (isNaN(balance)) return total;
          
  //         let adjustedBalance = balance;
  //         if (wallet.currency === 'TRX' && tronWalletsData[wallet.address]?.trxBalance) {
  //           adjustedBalance = parseFloat(tronWalletsData[wallet.address].trxBalance);
  //         }
          
  //         const currency = wallet.currency.split('_')[0];
  //         const price = cryptoPrices[currency];
          
  //         if (price) {
  //           return total + (adjustedBalance * price);
  //         }
          
  //         return total;
  //       }, 0);
  //     };
  //     const calculateVirtualWalletValues = () => {
  //       if (!virtualWallets?.length) return 0;
        
  //       const regularWalletKeys = new Set(
  //         dashboardData.wallets?.map(w => `${w.currency}-${w.address}`) || []
  //       );
        
  //       return virtualWallets.reduce((total, wallet) => {
  //         if (regularWalletKeys.has(`${wallet.currency}-${wallet.address}`)) return total;
          
  //         if (wallet.currency === 'USDT_TRON' && wallet.balance) {
  //           const balance = parseFloat(wallet.balance);
            
  //           if (!isNaN(balance) && balance > 0) {
  //             return total + (balance * (cryptoPrices.USDT || 1));
  //           }
  //         }
          
  //         return total;
  //       }, 0);
  //     };
  //     try {
  //       loadCachedExchangeRates();
  //       totalFiatValue = calculateFiatValues();
  //       totalCryptoValue = calculateCryptoWalletValues() + calculateRailCryptoValues() + calculateVirtualWalletValues();
  //       if (isMounted.current) {
  //         setCalculatedBalances({
  //           fiatBalance: totalFiatValue,
  //           cryptoBalance: totalCryptoValue,
  //           totalWealth: totalFiatValue + totalCryptoValue
  //         });
  //       }
  //     } catch (error) {
  //     }
  // };
  // Fetch SOL price
  useEffect(() => {
    const fetchSolPrice = async () => {
      try {
        const response = await fetch(
          'https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd'
        );
        const data = await response.json();
        if (data.solana?.usd) {
          setSolPrice(data.solana.usd);
        }
      } catch (error) {
        console.error('Error fetching SOL price:', error);
        setSolPrice(145); // Fallback price
      }
    };

    fetchSolPrice();
    const interval = setInterval(fetchSolPrice, 10 * 60 * 1000); // Update every 10 minutes
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    isMounted.current = true;
    const savedBalances = localStorage.getItem('tron-usdt-balances');
    if (savedBalances) {
      try {
        const parsed = JSON.parse(savedBalances);
        setPersistedUsdtBalances(parsed);
      } catch (error) {
        console.error('Error parsing persisted balances:', error);
      }
    }

    // Verificación del token
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) {
      console.error('No authentication token found');
      navigate('/login');
    }

    // Zenus account is now loaded from Redux store, no need to fetch here
    
    // Limpiar el componente al desmontarse
    return () => {
      console.log('Wallets component unmounting - cleaning up');
      isMounted.current = false;
      
      // Limpiar todos los temporizadores
      Object.values(tronFetchTimeoutRefs.current).forEach(timeout => {
        clearTimeout(timeout);
      });
      
      if (calculationTimeoutRef.current) {
        clearTimeout(calculationTimeoutRef.current);
      }
      
      if (virtualWalletsTimeoutRef.current) {
        clearTimeout(virtualWalletsTimeoutRef.current);
      }
    };
  }, [navigate]);
  //  const { data: dashboardData, isLoading: dashboardLoading, refetch } = useCryptoWallets()
  // const { data: dashboardData, isLoading: dashboardLoading, refetch } = useQuery({
  //   queryKey: ['dashboard'],
  //   queryFn: () => walletService.getDashboardData(),
  //   refetchOnWindowFocus: false,  // No recargar al volver a la ventana
  //   staleTime: 600000,            // Datos válidos por 10 minutos
  //   retry: 1,                     // Reducir el número de reintentos
  // });
  // const handleForceReload = useCallback(() => {
  //   // Verificar si ha pasado suficiente tiempo desde la última recarga
  //   const lastReloadTime = localStorage.getItem('last-wallet-reload-time');
  //   const now = Date.now();
  //   const minTimeBetweenReloads = 10000; // 10 segundos
    
  //   if (lastReloadTime && (now - parseInt(lastReloadTime)) < minTimeBetweenReloads) {
  //     toast.info("Please wait a moment before reloading again");
  //     return;
  //   }
    
  //   localStorage.setItem('last-wallet-reload-time', now.toString());
  //   localStorage.removeItem('dashboardData');
  //   localStorage.removeItem('dashboardDataTimestamp');
  //   // refetch();
  //   toast.info('Reloading wallet data...');
  // }, [refetch]);
  // const 
  // useEffect(() => {
  //   if (initialLoadRef.current) {
  //     console.log('Wallets component - initial data fetch');
  //     initialLoadRef.current = false;
  //     refetch();
  //   }
  // }, [refetch]);
  // const fetchTronWalletDetails = useCallback(async (address: string) => {
  //   if (!address || !isMounted.current) return null;
    
  //   try {
  //     // Verificar caché local para evitar peticiones frecuentes
  //     const cacheKey = `tron-wallet-details-${address}`;
  //     const cachedData = localStorage.getItem(cacheKey);
  //     const cachedTimestamp = localStorage.getItem(`${cacheKey}-timestamp`);
  //     const cacheStillValid = cachedTimestamp && (Date.now() - parseInt(cachedTimestamp)) < 300000; // 5 minutos
      
  //     if (cachedData && cacheStillValid) {
  //       console.log(`Using cached TRON wallet details for ${address} (age: ${(Date.now() - parseInt(cachedTimestamp)) / 1000}s)`);
  //       try {
  //         return JSON.parse(cachedData);
  //       } catch (e) {
  //         console.error('Error parsing cached wallet data:', e);
  //       }
  //     }
      
  //     // Verificar si ha pasado suficiente tiempo desde la última solicitud para esta dirección
  //     const lastUpdateKey = `last-tron-update-${address}`;
  //     const lastUpdateTime = localStorage.getItem(lastUpdateKey);
  //     const throttleTime = 300000; // 5 minutos
      
  //     if (lastUpdateTime && (Date.now() - parseInt(lastUpdateTime)) < throttleTime) {
  //       console.log(`Skipping TRON update for ${address}, last update was ${Math.floor((Date.now() - parseInt(lastUpdateTime))/1000)}s ago`);
  //       return null;
  //     }
      
  //     // Registrar la hora de esta actualización
  //     localStorage.setItem(lastUpdateKey, Date.now().toString());
      
  //     const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  //     if (!token) {
  //       console.error('No auth token found for TRON wallet fetch');
  //       return null;
  //     }

  //     console.log(`Fetching TRON wallet details for ${address}...`);
  //     const response = await fetch(`https://api.v2.belobaba.io/api/tron/wallet/${address}`, {
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'application/json'
  //       }
  //     });

  //     if (!response.ok) {
  //       throw new Error(`Failed to fetch TRON wallet details: ${response.statusText}`);
  //     }

  //     const data = await response.json();
  //     console.log(`Fetched TRON wallet data for ${address}:`, data);
      
  //     // Guardar en caché local
  //     localStorage.setItem(cacheKey, JSON.stringify(data));
  //     localStorage.setItem(`${cacheKey}-timestamp`, Date.now().toString());
      
  //     // MEJORA: Lógica para preservar balances positivos
  //     if (data && data.usdtBalance !== undefined && isMounted.current) {
  //       const newBalance = parseFloat(data.usdtBalance);
        
  //       // Verificar el balance persistido para esta dirección
  //       const persistedBalance = persistedUsdtBalances[address] 
  //         ? parseFloat(persistedUsdtBalances[address]) 
  //         : 0;
        
  //       // Verificar si tenemos datos previos en tronWalletsData
  //       const cachedWalletData = tronWalletsData[address];
  //       const cachedBalance = cachedWalletData?.usdtBalance 
  //         ? parseFloat(cachedWalletData.usdtBalance) 
  //         : 0;
        
  //       // Determinar el mejor balance a usar
  //       const currentHighestBalance = Math.max(persistedBalance, cachedBalance);
        
  //       // Si el nuevo balance es 0 pero teníamos un balance mayor, mantener el mayor
  //       if (newBalance === 0 && currentHighestBalance > 0) {
  //         console.log(`Preserving existing USDT balance ${currentHighestBalance} for ${address} (received 0)`);
  //         data.usdtBalance = currentHighestBalance.toString();
          
  //         // Actualizar el estado persistido
  //         if (isMounted.current) {
  //           setPersistedUsdtBalances(prev => ({
  //             ...prev,
  //             [address]: currentHighestBalance.toString()
  //           }));
  //         }
          
  //         // Guardar en localStorage para persistencia
  //         const updatedBalances = {
  //           ...persistedUsdtBalances,
  //           [address]: currentHighestBalance.toString()
  //         };
  //         localStorage.setItem('tron-usdt-balances', JSON.stringify(updatedBalances));
  //       } 
  //       // Si recibimos un balance mayor que 0, actualizarlo en nuestro estado persistido
  //       else if (newBalance > 0) {
  //         if (isMounted.current) {
  //           setPersistedUsdtBalances(prev => ({
  //             ...prev,
  //             [address]: newBalance.toString()
  //           }));
  //         }
          
  //         // Guardar en localStorage para persistencia
  //         const updatedBalances = {
  //           ...persistedUsdtBalances,
  //           [address]: newBalance.toString()
  //         };
  //         localStorage.setItem('tron-usdt-balances', JSON.stringify(updatedBalances));
  //       }
  //     }
      
  //     return data;
  //   } catch (error) {
  //     console.error(`Error fetching TRON wallet details for ${address}:`, error);
  //     return null;
  //   }
  // }, [persistedUsdtBalances, tronWalletsData]);
  // useEffect(() => {
  //   if (!dashboardData?.wallets || !isMounted.current || processingTronWalletsRef.current) {
  //     return;
  //   }
    
  //   // Buscar wallets TRON
  //   const tronWallets = dashboardData.wallets.filter(wallet => 
  //     wallet.currency === 'USDT_TRON' || wallet.currency === 'TRX'
  //   );
    
  //   if (tronWallets.length === 0) return;
    
  //   // Verificar si ha pasado suficiente tiempo desde la última actualización global
  //   const now = Date.now();
  //   const lastGlobalUpdate = localStorage.getItem('last-tron-global-update');
  //   const throttleTime = 300000; // 5 minutos
    
  //   if (lastGlobalUpdate && (now - parseInt(lastGlobalUpdate)) < throttleTime) {
  //     console.log(`Skipping TRON wallets global update, last update was ${Math.floor((now - parseInt(lastGlobalUpdate))/1000)}s ago`);
  //     return;
  //   }
    
  //   // Evitar que este efecto se ejecute más de una vez simultáneamente
  //   processingTronWalletsRef.current = true;
    
  //   // Guardar timestamp para la siguiente actualización global
  //   localStorage.setItem('last-tron-global-update', now.toString());
    
  //   // MEJORA CRÍTICA: Procesar wallets con un patrón espaciado para evitar loop de rerenderizado
  //   const processWallet = (index: number) => {
  //     if (!isMounted.current || index >= tronWallets.length) {
  //       processingTronWalletsRef.current = false;
  //       return;
  //     }
      
  //     const wallet = tronWallets[index];
      
  //     // Limpiar cualquier temporizador existente para esta wallet
  //     if (tronFetchTimeoutRefs.current[wallet.address]) {
  //       clearTimeout(tronFetchTimeoutRefs.current[wallet.address]);
  //     }
      
  //     // Establecer un nuevo temporizador con un retraso proporcional
  //     tronFetchTimeoutRefs.current[wallet.address] = setTimeout(async () => {
  //       if (!isMounted.current) return;
        
  //       const tronWalletData = await fetchTronWalletDetails(wallet.address);
  //       if (tronWalletData && isMounted.current) {
  //         // Actualizar datos sólo si el componente sigue montado
  //         setTronWalletsData(prev => {
  //           // Si hay datos actuales con balances positivos y tronWalletData tiene balances en 0,
  //           // preservamos los balances positivos
  //           const currentData = prev[wallet.address];
            
  //           // Agregar console.log para depuración
  //           console.log(`Actualizando datos de wallet TRON ${wallet.address}:`, {
  //             prevData: prev[wallet.address],
  //             newData: tronWalletData
  //           });
            
  //           if (currentData && tronWalletData) {
  //             if (currentData.usdtBalance && parseFloat(currentData.usdtBalance) > 0 &&
  //                 tronWalletData.usdtBalance && parseFloat(tronWalletData.usdtBalance) === 0) {
  //               tronWalletData.usdtBalance = currentData.usdtBalance;
  //             }
              
  //             if (currentData.trxBalance && parseFloat(currentData.trxBalance) > 0 &&
  //                 tronWalletData.trxBalance && parseFloat(tronWalletData.trxBalance) === 0) {
  //               tronWalletData.trxBalance = currentData.trxBalance;
  //             }
  //           }
            
  //           return {
  //             ...prev,
  //             [wallet.address]: tronWalletData
  //           };
  //         });
  //       }
        
  //       // Procesar la siguiente wallet después de un retraso
  //       const nextWalletDelay = 5000; // 5 segundos entre wallets
  //       tronFetchTimeoutRefs.current[`next-${wallet.address}`] = setTimeout(() => {
  //         if (isMounted.current) {
  //           processWallet(index + 1);
  //         }
  //         delete tronFetchTimeoutRefs.current[`next-${wallet.address}`];
  //       }, nextWalletDelay);
        
  //       delete tronFetchTimeoutRefs.current[wallet.address];
  //     }, index * 200); // Un pequeño retraso inicial para espaciar las solicitudes
  //   };
    
  //   // Iniciar el procesamiento con la primera wallet
  //   processWallet(0);
    
  //   // Limpiar temporizadores cuando el efecto se vuelva a ejecutar
  //   return () => {
  //     Object.values(tronFetchTimeoutRefs.current).forEach(timeout => {
  //       clearTimeout(timeout);
  //     });
  //     tronFetchTimeoutRefs.current = {};
  //     processingTronWalletsRef.current = false;
  //   };
  // }, [dashboardData?.wallets, fetchTronWalletDetails]);
  // useEffect(() => {
  //   if (!dashboardData?.wallets || !isMounted.current) return;
    
  //   // Limpiar cualquier temporizador pendiente
  //   if (virtualWalletsTimeoutRef.current) {
  //     clearTimeout(virtualWalletsTimeoutRef.current);
  //   }
    
  //   // Usar un temporizador para evitar múltiples actualizaciones
  //   virtualWalletsTimeoutRef.current = setTimeout(() => {
  //     if (!isMounted.current) return;
      
  //     const newVirtualWallets: WalletData[] = [];
      
  //     // Buscar wallets TRON y verificar si tienen USDT
  //     const tronWallets = dashboardData.wallets.filter(wallet => 
  //       wallet.currency === 'TRX'
  //     );
      
  //     // Si ya existen wallets USDT_TRON, obtener sus direcciones
  //     const existingUsdtAddresses = dashboardData.wallets
  //       .filter(wallet => wallet.currency === 'USDT_TRON')
  //       .map(wallet => wallet.address);
      
  //     // Para cada wallet TRX, verificar si tiene USDT y no existe ya una wallet USDT_TRON
  //     tronWallets.forEach(wallet => {
  //       // Si esta dirección ya tiene una wallet USDT_TRON, no crear virtual
  //       if (existingUsdtAddresses.includes(wallet.address)) {
  //         return;
  //       }
        
  //       // Verificar si hay un balance USDT persistido para esta dirección
  //       const persistedBalance = persistedUsdtBalances[wallet.address];
  //       if (persistedBalance && parseFloat(persistedBalance) > 0) {
  //         console.log(`Creating virtual USDT_TRON wallet for TRX address ${wallet.address} with persisted balance ${persistedBalance}`);
          
  //         // Crear wallet virtual de USDT_TRON con la misma dirección
  //         newVirtualWallets.push({
  //           currency: 'USDT_TRON',
  //           address: wallet.address,
  //           balance: persistedBalance,
  //           usdtBalance: persistedBalance,
  //           trxBalance: wallet.balance || '0',
  //           tronscanUrl: `https://tronscan.org/#/address/${wallet.address}`
  //         });
          
  //         return;
  //       }
        
  //       // Si no hay balance persistido, verificar los datos TRON actualizados
  //       const tronData = tronWalletsData[wallet.address];
  //       if (tronData && tronData.usdtBalance && parseFloat(tronData.usdtBalance) > 0) {
  //         console.log(`Creating virtual USDT_TRON wallet for TRX address ${wallet.address} with tronData balance ${tronData.usdtBalance}`);
          
  //         // Crear wallet virtual de USDT_TRON con la misma dirección
  //         newVirtualWallets.push({
  //           currency: 'USDT_TRON',
  //           address: wallet.address,
  //           balance: tronData.usdtBalance,
  //           usdtBalance: tronData.usdtBalance,
  //           trxBalance: tronData.trxBalance || wallet.balance || '0',
  //           tronscanUrl: `https://tronscan.org/#/address/${wallet.address}`
  //         });
  //       }
  //     });
      
  //     if (newVirtualWallets.length > 0 && isMounted.current) {
  //       // Comparar si realmente hay cambios para evitar re-renderizados innecesarios
  //       const currentAddresses = virtualWallets.map(w => w.address);
  //       const newAddresses = newVirtualWallets.map(w => w.address);
        
  //       if (JSON.stringify(currentAddresses) !== JSON.stringify(newAddresses)) {
  //         console.log(`Updating virtual wallets, found ${newVirtualWallets.length} new wallets`);
  //         setVirtualWallets(newVirtualWallets);
  //       }
  //     }
      
  //     virtualWalletsTimeoutRef.current = null;
  //   }, 2000); // 2 segundos de retraso para dejarlo respirar
    
  //   return () => {
  //     if (virtualWalletsTimeoutRef.current) {
  //       clearTimeout(virtualWalletsTimeoutRef.current);
  //       virtualWalletsTimeoutRef.current = null;
  //     }
  //   };
  // }, [dashboardData?.wallets, tronWalletsData, persistedUsdtBalances]); // Quitamos virtualWallets para evitar bucles
  // useEffect(() => {
  //   if (!dashboardData || dashboardLoading) return;
  //   if (calculationTimeoutRef.current) {
  //     clearTimeout(calculationTimeoutRef.current);
  //   }
  //   calculationTimeoutRef.current = setTimeout(() => {
  //     if (isMounted.current) {
  //       // calculateTotals();
  //     }
  //     calculationTimeoutRef.current = null;
  //   }, 300);
    
  //   return () => {
  //     if (calculationTimeoutRef.current) {
  //       clearTimeout(calculationTimeoutRef.current);
  //       calculationTimeoutRef.current = null;
  //     }
  //   };
  // }, [dashboardData, tronWalletsData, cryptoPrices, dashboardLoading, virtualWallets, persistedUsdtBalances]);

  // useEffect(() => {
  //   const fetchCryptoPrices = async () => {
  //     try {
  //       // Verificar caché de precios
  //       const cacheKey = 'crypto-prices';
  //       const cachedData = localStorage.getItem(cacheKey);
  //       const cachedTimestamp = localStorage.getItem(`${cacheKey}-timestamp`);
  //       const cacheStillValid = cachedTimestamp && (Date.now() - parseInt(cachedTimestamp)) < 1800000; // 30 minutos
        
  //       if (cachedData && cacheStillValid && isMounted.current) {
  //         try {
  //           const prices = JSON.parse(cachedData);
  //           console.log('Using cached crypto prices');
  //           setCryptoPrices(prices);
  //           return;
  //         } catch (e) {
  //           console.error('Error parsing cached prices:', e);
  //         }
  //       }
        
  //       // Si no hay caché válido, obtener nuevos precios
  //       const response = await fetch(
  //         'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,binancecoin,tether,tron&vs_currencies=usd&precision=6'
  //       );
        
  //       if (!response.ok) {
  //         throw new Error('Failed to fetch crypto prices');
  //       }
        
  //       const data = await response.json();
        
  //       const newPrices = {
  //         BTC: data.bitcoin?.usd || 65000,
  //         ETH: data.ethereum?.usd || 4000,
  //         BNB: data.binancecoin?.usd || 600,
  //         USDT: data.tether?.usd || 0.999,
  //         TRX: data.tron?.usd || 0.05,
  //       };
        
  //       // Guardar en caché
  //       localStorage.setItem(cacheKey, JSON.stringify(newPrices));
  //       localStorage.setItem(`${cacheKey}-timestamp`, Date.now().toString());
        
  //       if (isMounted.current) {
  //         setCryptoPrices(newPrices);
  //       }
  //     } catch (error) {
  //       console.error('Error fetching crypto prices:', error);
  //       // Mantener los valores predeterminados en caso de error
  //     }
  //   };

  //   fetchCryptoPrices();
  //   // calculateTotals()
  //   const priceInterval = setInterval(fetchCryptoPrices, 1800000); // 30 minutos
  //   return () => clearInterval(priceInterval);
  // }, []);
  const handleShowQRCode = useCallback(async (currency: string, address: string, network?: string) => {
    try {
      if (loading) return;

      setLoading(true);
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${address}`;

      if (isMounted.current) {
        setQRCodeData(qrCodeUrl);
        setShowQRCode(address);
        setQRCodeCurrency(currency);
        setQRCodeNetwork(network || '');
        setLoading(false);
      }
    } catch (error) {
      if (isMounted.current) {
        toast.error('Failed to load QR code');
        setLoading(false);
      }
    }
  }, [loading]);
  // const { data: railAccountsData, refetch: refetchRailAccounts } = useQuery({
  //   queryKey: ['railAccounts'],
  //   queryFn: () => mainService.getMainWallets(user.virtualDash)
  // });
  // useEffect(() => {
  //   const updateApplicationStates = async () => {
  //     console.log(railAccountsData)
  //     if (railAccountsData?.success && railAccountsData.accounts) {
  //       const reclassifiedAccounts = getReclassifiedAccounts(railAccountsData.accounts);
  //       setRailAccounts(reclassifiedAccounts)
  //     }
  //   };
  //   updateApplicationStates();
  // }, [railAccountsData]);
  const closeQRModal = useCallback(() => {
    setShowQRCode(null);
    setQRCodeData(null);
  }, []);
  const getCurrencyIcon = (currency: string) => {
    switch (currency.toUpperCase()) {
      case 'BTC':
        return <BitcoinIcon size={24} color="#B3A369" />;
      case 'ETH':
        return <EthereumIcon size={24} color="#B3A369" />;
      case 'USDT_ERC20':
      case 'USDT':
        return <TetherIcon size={24} color="#26A17B" />;
      case 'USDT_TRON':
        return <TetherIcon size={24} color="#26A17B" />;
      case 'TRX':
        return <TronIcon size={24} color="#FF060A" />;
      case 'BNB':
        return <BinanceIcon size={24} color="#F3BA2F" />;
      case 'USDT_BEP20':
        return <BinanceIcon size={36} color="#2f99f3" />;
      case 'USD':
        return <div className="scale-125"><ReactCountryFlag countryCode="US" svg /></div>;
      case 'EUR':
        return <div className="scale-125"><ReactCountryFlag countryCode="EU" svg /></div>;
      case 'GBP':
        return <div className="scale-125"><ReactCountryFlag countryCode="GB" svg /></div>;
      case 'USDC':
        return <TetherIcon size={24} color="#2775CA" />;
        default:
        return null;
    }
  };
  const getCurrencyName = (currency: string) => {
    switch (currency.toUpperCase()) {
      case 'BTC':
        return 'Bitcoin';
      case 'ETH':
        return 'Ethereum';
      case 'USDT_ERC20':
        return 'USDT (ERC20)';
      case 'USDT_TRON':
        return 'USDT (TRC20)';
      case 'BNB':
        return 'BNB';
      case 'USDT_BEP20':
        return 'USDT (BEP20)'
      case 'TRX':
        return 'TRX';
      case 'USD':
        return 'US Dollar';
      case 'EUR':
        return 'Euro';
      case 'GBP':
        return 'British Pound';
      default:
        return currency;
    }
  };
  const getWalletDisplayBalance = useCallback((wallet: WalletData) => {
    // MEJORA: Para wallets USDT_TRON, verificar primero datos actualizados
    if (wallet.currency === 'USDT_TRON') {
      // Verificar primero si tenemos datos actualizados para esta wallet
      const tronData = tronWalletsData[wallet.address];
      if (tronData && tronData.usdtBalance !== undefined && parseFloat(tronData.usdtBalance) > 0) {
        return {
          balance: tronData.usdtBalance,
          currency: 'USDT'
        };
      }
      
      // Si no hay datos de tronWalletsData, comprobar balance persistido
      const persistedBalance = persistedUsdtBalances[wallet.address];
      if (persistedBalance && parseFloat(persistedBalance) > 0) {
        return {
          balance: persistedBalance,
          currency: 'USDT'
        };
      }
      
      // Si no hay datos actualizados pero tenemos usdtBalance en el objeto original
      if (wallet.usdtBalance !== undefined && parseFloat(wallet.usdtBalance) > 0) {
        return {
          balance: wallet.usdtBalance,
          currency: 'USDT'
        };
      }
      
      // Si todo lo demás falla, usar el balance estándar
      return {
        balance: wallet.balance || '0',
        currency: 'USDT'
      };
    }
    
    // Para wallets TRX, intentar usar los datos actualizados
    if (wallet.currency === 'TRX') {
      // Comprobar si tenemos datos actualizados para esta wallet
      const tronData = tronWalletsData[wallet.address];
      if (tronData && tronData.trxBalance !== undefined) {
        return {
          balance: tronData.trxBalance,
          currency: 'TRX'
        };
      }
      
      // Si no hay datos actualizados pero tenemos trxBalance en el objeto original
      if (wallet.trxBalance !== undefined) {
        return {
          balance: wallet.trxBalance,
          currency: 'TRX'
        };
      }
    }
    
    // Para wallets de BTC, aplicar lógica especial de BTC/mBTC
    if (wallet.currency === 'BTC') {
      const btcBalance = parseFloat(wallet.balance || '0');
      
      // Para cantidades ≥ 0.099 BTC, mostrar en BTC
      if (btcBalance >= 0.099) {
        return {
          balance: btcBalance.toString(),
          currency: 'BTC'
        };
      } 
      // Para cantidades ≤ 0.098 BTC, convertir a mBTC
      else {
        const mBtcBalance = (btcBalance * 1000).toString();
        return {
          balance: mBtcBalance,
          currency: 'mBTC'
        };
      }
    }
    
    // Para otras criptomonedas, mostrar el balance normal
    return {
      balance: wallet.balance,
      currency: wallet.currency.split('_')[0]
    };
  }, [persistedUsdtBalances, tronWalletsData]);
  const formatBalance = (balance: string, currency: string) => {
    const numericBalance = parseFloat(balance || '0');
    
    if (currency === 'BTC') {
      return numericBalance.toLocaleString(undefined, { minimumFractionDigits: 7, maximumFractionDigits: 7 });
    } else if (currency === 'mBTC') {
      return numericBalance.toLocaleString(undefined, { minimumFractionDigits: 7, maximumFractionDigits: 7 });
    } else {
      return numericBalance.toLocaleString(undefined, { minimumFractionDigits: 5, maximumFractionDigits: 5 });
    }
  };
  const getBlockchainExplorerUrl = (currency: string, address: string) => {
    switch (currency.toUpperCase()) {
      case 'BTC':
        return `https://www.blockchain.com/explorer/addresses/btc/${address}`;
      case 'ETH':
      case 'USDT_ERC20':
        return `https://etherscan.io/address/${address}`;
      case 'BNB':
        return `https://bscscan.com/address/${address}`;
      case 'TRX':
      case 'USDT_TRON':
        return `https://tronscan.org/#/address/${address}`;
      default:
        return '';
    }
  };
  const getBlockchainExplorerName = (currency: string) => {
    switch (currency.toUpperCase()) {
      case 'BTC':
        return 'Blockchain.com';
      case 'ETH':
      case 'USDT_ERC20':
        return 'Etherscan';
      case 'BNB':
        return 'BscScan';
      case 'TRX':
      case 'USDT_TRON':
        return 'TronScan';
      default:
        return '';
    }
  };
  useEffect(()=>{
    const result = [];
    if(cryptoWallets.data){
      Object.keys(cryptoWallets.data).forEach(network => {
        cryptoWallets.data[network].forEach(wallet => {
          Object.keys(wallet.balances).forEach(currency => {
            // Mapear correctamente las currencies para tokens
            let displayCurrency = currency;

            // Manejar tokens USDT en diferentes redes
            if (currency === 'usdt') {
              if (network === 'ethereum') {
                displayCurrency = 'USDT_ERC20';
              } else if (network === 'bnb') {
                displayCurrency = 'USDT_BEP20';
              } else if (network === 'tron') {
                displayCurrency = 'USDT_TRC20';
              } else if (network === 'solana') {
                displayCurrency = 'USDT_SPL';
              }
            }
            // Manejar tokens USDC
            else if (currency === 'usdc') {
              if (network === 'ethereum') {
                displayCurrency = 'USDC_ERC20';
              } else if (network === 'solana') {
                displayCurrency = 'USDC_SPL';
              }
            }
            // Convertir currency a mayúsculas para monedas nativas
            else {
              displayCurrency = currency.toUpperCase();
            }

            result.push({
              id: wallet.id,
              userId: wallet.userId,
              network: network,
              address: wallet.address,
              currency: displayCurrency,
              balance: wallet.balances[currency]
            });
          });
        });
      });
      setWalletList(result)
    }
  },[cryptoWallets])
  useEffect(()=>{
    let cryptoTotal = 0;

    if (walletList && walletList.length > 0) {
      console.log('Calculating total balance from walletList:', walletList);
      console.log('Crypto prices:', cryptoPrices);
      console.log('SOL price:', solPrice);

      cryptoTotal = walletList.reduce((sum, wallet) => {
      const balance = parseFloat(wallet.balance || "0");

      // Skip if balance is 0
      if (balance === 0) return sum;

      // Convert to USD based on currency
      let usdValue = 0;
      const currency = wallet.currency.toUpperCase();

      // Log each calculation for debugging
      console.log(`Wallet ${wallet.address} - Currency: ${currency}, Balance: ${balance}`);

      switch(currency) {
        case 'BTC':
        case 'BITCOIN':
          usdValue = balance * (cryptoPrices?.BTC || 0);
          console.log(`  BTC: ${balance} * ${cryptoPrices?.BTC} = ${usdValue}`);
          break;
        case 'ETH':
        case 'ETHEREUM':
          usdValue = balance * (cryptoPrices?.ETH || 0);
          console.log(`  ETH: ${balance} * ${cryptoPrices?.ETH} = ${usdValue}`);
          break;
        case 'BNB':
          usdValue = balance * (cryptoPrices?.BNB || 0);
          console.log(`  BNB: ${balance} * ${cryptoPrices?.BNB} = ${usdValue}`);
          break;
        case 'TRX':
        case 'TRON':
          usdValue = balance * (cryptoPrices?.TRX || 0);
          console.log(`  TRX: ${balance} * ${cryptoPrices?.TRX} = ${usdValue}`);
          break;
        case 'SOL':
        case 'SOLANA':
          usdValue = balance * (solPrice || 145);
          console.log(`  SOL: ${balance} * ${solPrice} = ${usdValue}`);
          break;
        case 'USDT':
        case 'USDT_ERC20':
        case 'USDT_TRC20':
        case 'USDT_BEP20':
        case 'USDC':
        case 'USDC_ERC20':
        case 'USDC_TRC20':
        case 'USDC_BEP20':
          // Stablecoins are 1:1 with USD
          usdValue = balance * 1;
          console.log(`  Stablecoin (${currency}): ${balance} * 1 = ${usdValue}`);
          break;
        default:
          console.log(`  Unknown currency ${currency}, treating as USD`);
          usdValue = balance;
      }

      return sum + usdValue;
    }, 0);
    }

    // IMPORTANT: Calculate total ALWAYS, not just when walletList exists
    // Add fiat balance (USD from Zenus account) and MasterCard balance
    // NOTE: MasterCard balance is ALREADY converted to USD in Redux (assetsReducer line 85)
    // Do NOT convert again here!
    const mastercardBalanceInUSD = mastercardBalance; // Already in USD from Redux
    const totalBalance = cryptoTotal + fiatBalance + mastercardBalanceInUSD;

    console.log('=== TOTAL BALANCE CALCULATION ===');
    console.log('Crypto total:', cryptoTotal);
    console.log('Fiat balance (USD):', fiatBalance);
    console.log('MasterCard balance (already in USD):', mastercardBalance);
    console.log('Total balance in USD:', totalBalance);
    console.log('=================================');
    setTotalBalance(totalBalance)
  },[walletList, cryptoPrices, solPrice, fiatBalance, mastercardBalance])
  // if (assets.status !=="succeeded") {
  //   return (
  //    <IsLoading />
  //   );
  // }
  // const allWallets = [...(dashboardData?.wallets || []), ...virtualWallets];
  // const uniqueWallets = allWallets.reduce((acc: WalletData[], current: WalletData) => {
  //   const x = acc.find(item => item.currency === current.currency && item.address === current.address);
  //   if (!x) {
  //     return acc.concat([current]);
  //   } else {
  //     return acc;
  //   }
  // }, []).sort((a, b) => {
  //   const currencyOrder: Record<string, number> = {
  //     'BTC': 1,
  //     'ETH': 2,
  //     'USDT_ERC20': 3,
  //     'TRX': 4,
  //     'USDT_TRON': 5,
  //     'BNB': 6
  //   };
  //   const orderA = currencyOrder[a.currency] || 999;
  //   const orderB = currencyOrder[b.currency] || 999;
  //   return orderA - orderB;
  // });
  
  return (
    <div className='sm:p-[30px] h-full'>
      <div className='flex justify-between items-center mb-[20px]'>
        <h1 className='text-[32px] m-0'>{t('wallets.myWallets')}</h1>
        {/* <button 
          onClick={handleForceReload}
          className="dashboard-btn dashboard-btn-sm ml-auto"
        >
          Reload Data
        </button> */}
      </div>
      <div className='sm:grid  sm:gap-5 sm:mb-7'>
        <div className='p-[20px] bg-gray-100 rounded-lg shadow-sm mb-0.5'>
          <h3 className='text-base text-gray-600'>Total Value</h3>
          <p className='text-2xl font-bold text-[#B3A369]'>
            {formatCurrency(totalBalance)}
          </p>
        </div>
        
        {/* <div className='p-[20px] bg-gray-100 rounded-lg shadow-sm mb-0.5'>
          <h3 className='text-base text-gray-600'>Fiat Balance</h3>
          <p className='text-2xl font-bold'>
            {formatCurrency(mainWallets.fiatWallets.balance)}
          </p>
        </div>
        
        <div className='p-[20px] bg-gray-100 rounded-lg shadow-sm mb-0.5'>
          <h3 className='text-base text-gray-600'>Crypto Balance</h3>
          <p className='text-2xl font-bold'>
            {formatCurrency(mainWallets.custodianWallets.balance + cryptoWallets.balance)}
          </p>
        </div> */}
      </div>
      {status !=="succeeded" ? (
         <IsLoading />
      ):(
        <>
           {/* FIAT Wallets Section */}
           {(zenusAccount?.account || fiatBalance > 0) && (
             <div className='flex flex-col gap-[15px] pb-4'>
               <h4 className='text-2xl mt-4'>FIAT Wallets</h4>
               <Link to='/dashboard/wallets/usd' className='no-underline'>
                 <div className='bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer'>
                   <div className='flex items-center justify-between'>
                     <div className='flex items-center gap-3'>
                       <div className='w-10 h-10 bg-green-100 rounded-full flex items-center justify-center'>
                         <span className='text-green-600 font-bold text-lg'>$</span>
                       </div>
                       <div>
                         <div className='font-semibold'>USD Account</div>
                         <div className='text-sm text-gray-500'>USA Bank</div>
                       </div>
                     </div>
                     <div className='text-right'>
                       <div className='font-semibold text-lg text-[#B3A369]'>
                         ${fiatBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                       </div>
                       <div className='text-sm text-gray-500'>Available</div>
                     </div>
                   </div>
                   {zenusAccount?.account?.accountNumber && (
                     <div className='mt-3 pt-3 border-t border-gray-100 flex items-center justify-between'>
                       <span className='text-xs text-gray-500'>Account: ***{zenusAccount.account.accountNumber.slice(-4)}</span>
                       <span className='text-xs text-blue-600'>View Details →</span>
                     </div>
                   )}
                 </div>
               </Link>
             </div>
           )}

           {/* Crypto Wallets Section */}
           <div className='flex flex-col gap-[15px] pb-8'>
              <h4 className='text-2xl   mt-4 '>Crypto Wallets</h4>
              {walletList && walletList.map((item: WalletItemType) => (
                <WalletDetailsItem
                  key={`${item.network}-${item.currency}-${item.address}`}
                  wallet={item}
                  handleShowQRCode={(currency, address) => handleShowQRCode(currency, address, item.network)}
                  // getWalletDisplayBalance={getWalletDisplayBalance}
                  // getCurrencyIcon={getCurrencyIcon}
                  // getCurrencyName={getCurrencyName}
                  // getBlockchainExplorerUrl={getBlockchainExplorerUrl}
                  // getBlockchainExplorerName={getBlockchainExplorerName}
                  formatBalance={formatBalance}
                />
              ))}



            </div>
          {/* {cryptoWallets.data ? (
            <div className='flex flex-col gap-[15px] pb-8'>
              <h4 className='text-2xl   mt-4 '>{t('wallets.cryptoWallets')}</h4>
            {cryptoWallets.data.map((wallet: WalletData, index: number) => {
              return (
                <WalletDetailsItem
                  key={`${wallet.currency}-${wallet.address}-${index}`}
                  wallet={wallet}
                  index={index}
                  handleShowQRCode={handleShowQRCode}
                  getWalletDisplayBalance={getWalletDisplayBalance}
                  getCurrencyIcon={getCurrencyIcon}
                  getCurrencyName={getCurrencyName}
                  getBlockchainExplorerUrl={getBlockchainExplorerUrl}
                  getBlockchainExplorerName={getBlockchainExplorerName}
                  formatBalance={formatBalance}
                />
              );
            })}
          </div>
          ) : (
            <div className='p-10 text-center bg-gray-100 rounded-lg'>
              <p className='text-lg text-gray-600 mb-5'>
                You don't have any wallets yet.
              </p>
              <Link to="/dashboard">
                <button className="dashboard-btn dashboard-btn-default">
                  Create a Wallet
                </button>
              </Link>
            </div>
          )} */}
        </>
      )}
      
      {showQRCode && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]'>
          <div className='bg-white p-[30px] rounded-[10px] w-[90%] max-w-[450px] flex flex-col items-center'>
            <h3 className='mb-5 text-xl'>{t('wallets.qrCode')}</h3>

            {qrCodeData ? (
              <img
                src={qrCodeData}
                alt="Wallet QR Code"
               className='w-[200px] h-[200px] mb-5'
              />
            ) : (
              <div className='w-[200px] h-[200px] flex items-center justify-center'>
                <p>Loading QR code...</p>
              </div>
            )}

            <p className='text-sm text-gray-600 mb-3 break-all text-center font-mono'>
              {showQRCode}
            </p>

            {/* Network Warning */}
            {qrCodeCurrency && qrCodeNetwork && (() => {
              const networkInfo = getNetworkInfo(qrCodeCurrency, qrCodeNetwork);
              const messageKey = getWarningMessageKey(networkInfo);

              return (
                <div className='w-full bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-5'>
                  <div className='flex'>
                    <div className='flex-shrink-0'>
                      <svg className='h-5 w-5 text-yellow-400' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
                        <path fillRule='evenodd' d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z' clipRule='evenodd' />
                      </svg>
                    </div>
                    <div className='ml-3'>
                      <p className='text-sm text-yellow-700 font-semibold mb-1'>
                        {t(`wallets.qrWarning.${messageKey}`, {
                          tokenName: networkInfo.tokenName,
                          networkName: networkInfo.networkName
                        })}
                      </p>
                      <p className='text-xs text-yellow-600'>
                        {t('wallets.qrWarning.permanentLoss')}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

            <button
              onClick={closeQRModal}
              className="dashboard-btn dashboard-btn-default"
            >
              Close
            </button>
          </div>
        </div>
      )}

    </div>
  );
}